import { MIN_WIDTH_MD } from '@SBGSports/referee-design-tokens';
import { css } from 'linaria';
import { Variables } from '../index';
import { TypographyOwnProps } from './typography';

export const fontFamily =
    "'Mark Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif";

export const base = css`
    margin: 0;
    padding: 0;
    font-family: ${fontFamily};

    a,
    a:link {
        color: ${Variables.css.textLink};
        text-transform: none;
        text-decoration: underline;
        font-weight: 700;
    }

    a.secondary,
    a.secondary:link {
        color: ${Variables.css.textBody};
        text-transform: none;
        text-decoration: underline;
        font-weight: 700;
    }
`;

const heading1 = css`
    font-weight: 800;
    font-size: 40px;
    line-height: 52px;
    letter-spacing: 0.5px;
    font-stretch: condensed;

    @media screen and (min-width: ${MIN_WIDTH_MD}px) {
        font-size: 32px;
        line-height: 42px;
    }
`;

const heading2 = css`
    font-weight: 800;
    font-size: 32px;
    line-height: 42px;
    letter-spacing: 0.5px;
    font-stretch: condensed;

    @media screen and (min-width: ${MIN_WIDTH_MD}px) {
        font-size: 24px;
        line-height: 32px;
    }
`;

const heading3 = css`
    font-weight: 800;
    font-size: 24px;
    line-height: 32px;
    letter-spacing: 0.5px;
    font-stretch: condensed;

    @media screen and (min-width: ${MIN_WIDTH_MD}px) {
        font-size: 20px;
        line-height: 28px;
        letter-spacing: 0px;
    }
`;

const heading4 = css`
    font-weight: 700;
    font-size: 18px;
    line-height: 24px;

    @media screen and (min-width: ${MIN_WIDTH_MD}px) {
        font-weight: 700;
        font-size: 20px;
        line-height: 28px;
    }
`;

const heading5 = css`
    font-weight: 700;
    font-size: 16px;
    line-height: 24px;
`;

const heading6 = css`
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
`;

const body1 = css`
    font-size: 14px;
    line-height: 20px;
`;

const body2 = css`
    font-weight: 700;
    font-size: 14px;
    line-height: 20px;
`;

const label1 = css`
    font-weight: 700;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
`;

const label2 = css`
    font-weight: 700;
    font-size: 12px;
    line-height: 16px;
`;

const caption1 = css`
    font-size: 12px;
    line-height: 16px;
`;

const caption2 = css`
    font-size: 10px;
    line-height: 16px;

    @media screen and (min-width: ${MIN_WIDTH_MD}px) {
        font-size: 8px;
        line-height: 12px;
    }
`;

const button = css`
    font-weight: 700;
    font-size: 14px;
    line-height: 1;
    letter-spacing: 2px;
    text-transform: uppercase;
    display: inline-block;
    height: auto;
`;

export const variants: Record<TypographyOwnProps['variant'], string> = {
    'heading-1': heading1,
    'heading-2': heading2,
    'heading-3': heading3,
    'heading-4': heading4,
    'heading-5': heading5,
    'heading-6': heading6,
    'label-1': label1,
    'label-2': label2,
    'caption-1': caption1,
    'caption-2': caption2,
    'body-1': body1,
    'body-2': body2,
    button: button,
};

export const colors: Record<TypographyOwnProps['color'], string> = {
    body: css`
        color: ${Variables.css.textBody};
    `,
    secondary: css`
        color: ${Variables.css.textSecondary};
    `,
    tertiary: css`
        color: ${Variables.css.textTertiary};
    `,
    alert: css`
        color: ${Variables.css.alertText};
    `,
    information: css`
        color: ${Variables.css.informationText};
    `,
    warning: css`
        color: ${Variables.css.warningText};
    `,
    success: css`
        color: ${Variables.css.successText};
    `,
    inherit: css`
        color: inherit;
    `,
    'catapult-black': css`
        color: ${Variables.css.catapultBlack};
    `,
    'catapult-white': css`
        color: ${Variables.css.catapultWhite};
    `,
    'catapult-orange': css`
        color: ${Variables.css.catapultOrange};
    `,
};

export const align: Record<TypographyOwnProps['align'], string> = {
    start: css`
        text-align: start;
    `,
    center: css`
        text-align: center;
    `,
    end: css`
        text-align: end;
    `,
};

export const lineClampVar = '--lineClamp';
export const lineClamp = css`
    display: -webkit-box;
    -webkit-line-clamp: var(${lineClampVar});
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-all;
`;

export const expandable = css`
    display: flex;
    align-items: flex-end;
`;

export const expandableTypography = css`
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: pre;
    flex: 1 0 0px;
`;

export const expandableButton = css`
    position: relative;
    color: ${Variables.css.catapultOrange};
    text-decoration: underline;
    appearance: none;
    background: transparent;
    border: 0;
    margin: 0;
    padding: 0;
    flex-shrink: 0;
    font-weight: 500;

    &:hover {
        cursor: pointer;
    }
`;
